import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wrench, Plus, Eye, Printer, Inbox, ClipboardCheck, CheckCircle2, XCircle, FileText, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { qmsRqpService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATO_BADGE = {
  'Aperta': 'bg-blue-100 text-blue-800 border-blue-300',
  'Stampata / Consegnata a produzione': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'Rientrata da produzione': 'bg-purple-100 text-purple-800 border-purple-300',
  'In ricontrollo qualità': 'bg-amber-100 text-amber-800 border-amber-300',
  'Chiusa conforme': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Chiusa non conforme': 'bg-red-100 text-red-800 border-red-300',
  'Annullata': 'bg-slate-200 text-slate-600 border-slate-300',
};

const PRIORITA_BADGE = {
  Urgente: 'bg-red-100 text-red-800 border-red-300',
  Normale: 'bg-slate-100 text-slate-700 border-slate-300',
  Bassa: 'bg-slate-50 text-slate-500 border-slate-200',
};

export default function QmsProductionRequestsPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'quality');
  const canCreate = hasRole('admin', 'quality', 'operator');

  const [items, setItems] = useState([]);
  const [enums, setEnums] = useState(null);
  const [filters, setFilters] = useState({ stato: '', priorita: '', impatto: '' });

  const [detailOpen, setDetailOpen] = useState(false);
  const [active, setActive] = useState(null);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState(initManual());
  const [returnForm, setReturnForm] = useState(initReturn());
  const [recontrolForm, setRecontrolForm] = useState(initRecontrol());
  const [closeForm, setCloseForm] = useState({ esito_finale_richiesta: 'Conforme', note_chiusura: '' });
  const [activeAction, setActiveAction] = useState(null);  // 'return' | 'recontrol' | 'close' | 'cancel' | null
  const [cancelMotivo, setCancelMotivo] = useState('');

  function initManual() {
    return {
      customer_code: '', customer_name: '', product_code: '', product_description: '',
      revision: '', id_configurazione: '', commessa: '', lotto: '', fase_processo: '',
      motivo_richiesta: '', attivita_richiesta: '', quantita_interessata: '',
      priorita: 'Normale', impatto_operativo: 'Quando possibile',
      richiede_ricontrollo_qualita: true, scadenza_richiesta: '', note_qualita: '',
    };
  }
  function initReturn() {
    return {
      attivita_eseguita_produzione: '', quantita_lavorata: '', quantita_scartata: '',
      note_produzione: '', operatore_produzione: '',
    };
  }
  function initRecontrol() {
    return { esito_ricontrollo: 'Conforme', note_ricontrollo: '' };
  }

  useEffect(() => {
    qmsRqpService.getEnums().then(r => setEnums(r.data));
    load();
  }, []);
  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    try {
      const params = {};
      if (filters.stato) params.stato = filters.stato;
      if (filters.priorita) params.priorita = filters.priorita;
      if (filters.impatto) params.impatto = filters.impatto;
      const r = await qmsRqpService.list(params);
      setItems(r.data || []);
    } catch (e) { /* ignore */ }
  };

  const openDetail = async (it) => {
    try {
      const r = await qmsRqpService.getById(it.id);
      setActive(r.data);
      setReturnForm(initReturn());
      setRecontrolForm(initRecontrol());
      setCloseForm({ esito_finale_richiesta: 'Conforme', note_chiusura: '' });
      setActiveAction(null);
      setCancelMotivo('');
      setDetailOpen(true);
    } catch { toast.error('Errore caricamento'); }
  };

  const handleDownloadPdf = async (id, numero) => {
    try {
      const r = await qmsRqpService.pdf(id);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `RQP_${numero || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      load();
      if (active && active.id === id) openDetail({ id });
    } catch { toast.error('Errore generazione PDF'); }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...manualForm };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.quantita_interessata != null) payload.quantita_interessata = Number(payload.quantita_interessata);
      // mandatory fields
      if (!payload.customer_code || !payload.product_code || !payload.motivo_richiesta || !payload.attivita_richiesta) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }
      await qmsRqpService.createManual(payload);
      toast.success('Richiesta creata');
      setManualOpen(false);
      setManualForm(initManual());
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleReturn = async () => {
    if (!returnForm.attivita_eseguita_produzione) { toast.error('Inserisci attività eseguita'); return; }
    try {
      const payload = { ...returnForm };
      if (payload.quantita_lavorata !== '') payload.quantita_lavorata = Number(payload.quantita_lavorata);
      if (payload.quantita_scartata !== '') payload.quantita_scartata = Number(payload.quantita_scartata);
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await qmsRqpService.registerReturn(active.id, payload);
      toast.success('Rientro registrato');
      setActiveAction(null);
      openDetail({ id: active.id });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleRecontrol = async () => {
    try {
      await qmsRqpService.recontrol(active.id, recontrolForm);
      toast.success('Ricontrollo registrato');
      setActiveAction(null);
      openDetail({ id: active.id });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleClose = async () => {
    try {
      await qmsRqpService.close(active.id, closeForm);
      toast.success('Richiesta chiusa');
      setActiveAction(null);
      openDetail({ id: active.id });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleCancel = async () => {
    if (!cancelMotivo) { toast.error('Motivo annullamento obbligatorio'); return; }
    try {
      await qmsRqpService.cancel(active.id, { motivo_annullamento: cancelMotivo });
      toast.success('Richiesta annullata');
      setActiveAction(null);
      openDetail({ id: active.id });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const kpis = useMemo(() => ({
    aperte: items.filter(x => x.stato_richiesta === 'Aperta').length,
    urgenti: items.filter(x => x.priorita === 'Urgente' && !x.stato_richiesta.startsWith('Chiusa') && x.stato_richiesta !== 'Annullata').length,
    fermo: items.filter(x => x.impatto_operativo === 'Fermo macchina' && !x.stato_richiesta.startsWith('Chiusa')).length,
    in_ricontrollo: items.filter(x => x.stato_richiesta === 'In ricontrollo qualità').length,
  }), [items]);

  if (!enums) return <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;

  const isClosed = active && (active.stato_richiesta?.startsWith('Chiusa') || active.stato_richiesta === 'Annullata');

  return (
    <div className="space-y-6" data-testid="qms-rqp-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="w-6 h-6" />Richieste Qualità a Produzione</h1>
          <p className="text-sm text-slate-500">Foglio operativo cartaceo per la produzione, tracciabilità lato qualità.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setManualOpen(true)} data-testid="new-rqp-btn">
            <Plus className="w-4 h-4 mr-1" />Nuova richiesta manuale
          </Button>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500 tracking-wider">Aperte</p><p className="text-3xl font-bold text-blue-700">{kpis.aperte}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500 tracking-wider">Urgenti</p><p className="text-3xl font-bold text-red-700">{kpis.urgenti}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500 tracking-wider">Fermo macchina</p><p className="text-3xl font-bold text-orange-700">{kpis.fermo}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-slate-500 tracking-wider">In ricontrollo</p><p className="text-3xl font-bold text-amber-700">{kpis.in_ricontrollo}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Filter className="w-4 h-4" />Filtri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Select value={filters.stato || '__all'} onValueChange={v => setFilters({ ...filters, stato: v === '__all' ? '' : v })}>
            <SelectTrigger data-testid="filter-stato"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Tutti gli stati</SelectItem>{enums.stato_richiesta.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priorita || '__all'} onValueChange={v => setFilters({ ...filters, priorita: v === '__all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Tutte le priorità" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Tutte le priorità</SelectItem>{enums.priorita.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.impatto || '__all'} onValueChange={v => setFilters({ ...filters, impatto: v === '__all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="Tutti gli impatti" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Tutti gli impatti</SelectItem>{enums.impatto_operativo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setFilters({ stato: '', priorita: '', impatto: '' })}>Azzera filtri</Button>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Richieste ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessuna richiesta</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uppercase tracking-wider text-slate-500 border-b text-xs">
                    <th className="py-2 pr-2">N°</th>
                    <th className="py-2 pr-2">Data</th>
                    <th className="py-2 pr-2">Cliente / Prodotto</th>
                    <th className="py-2 pr-2">Commessa / Lotto</th>
                    <th className="py-2 pr-2">Fase</th>
                    <th className="py-2 pr-2">Priorità</th>
                    <th className="py-2 pr-2">Impatto</th>
                    <th className="py-2 pr-2">Stato</th>
                    <th className="py-2 pr-2">Creato da</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(it)} data-testid={`rqp-row-${it.id}`}>
                      <td className="py-2 pr-2 font-mono font-bold">{it.numero_richiesta}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{(it.data_creazione || '').slice(0, 10)}</td>
                      <td className="py-2 pr-2 text-xs">
                        <p className="font-medium">{it.customer_code} · {it.customer_name || '—'}</p>
                        <p className="text-slate-500">{it.product_code} {it.revision ? `· rev ${it.revision}` : ''}</p>
                      </td>
                      <td className="py-2 pr-2 text-xs">{it.commessa || '—'} / {it.lotto || '—'}</td>
                      <td className="py-2 pr-2 font-mono text-xs">{it.fase_processo || '—'}</td>
                      <td className="py-2 pr-2"><Badge className={`${PRIORITA_BADGE[it.priorita]} border text-[10px]`}>{it.priorita}</Badge></td>
                      <td className="py-2 pr-2 text-xs">{it.impatto_operativo}</td>
                      <td className="py-2 pr-2"><Badge className={`${STATO_BADGE[it.stato_richiesta]} border text-[10px]`}>{it.stato_richiesta}</Badge></td>
                      <td className="py-2 pr-2 text-xs font-mono">{it.creato_da || '—'}</td>
                      <td className="py-2 pr-2 space-x-1 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openDetail(it)} data-testid={`view-${it.id}`}><Eye className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadPdf(it.id, it.numero_richiesta)} data-testid={`pdf-${it.id}`}><Printer className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog with workflow actions */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" />{active.numero_richiesta}</DialogTitle>
                <DialogDescription>Creata {(active.data_creazione || '').slice(0, 16).replace('T', ' ')} da {active.creato_da}</DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap gap-2 items-center">
                <Badge className={`${STATO_BADGE[active.stato_richiesta]} border`}>{active.stato_richiesta}</Badge>
                <Badge className={`${PRIORITA_BADGE[active.priorita]} border`}>Priorità: {active.priorita}</Badge>
                <Badge className="bg-slate-100 text-slate-700 border border-slate-300 text-xs">Impatto: {active.impatto_operativo}</Badge>
                {active.richiede_ricontrollo_qualita && <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs">Richiede ricontrollo</Badge>}
                {active.scadenza_richiesta && <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">Scad. {active.scadenza_richiesta.slice(0, 10)}</Badge>}
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">Prodotto</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div><span className="text-slate-500">Cliente:</span> <b>{active.customer_code}</b> {active.customer_name}</div>
                  <div><span className="text-slate-500">Prodotto:</span> <b>{active.product_code}</b> {active.product_description}</div>
                  <div><span className="text-slate-500">Revisione:</span> <b>{active.revision || '—'}</b></div>
                  <div><span className="text-slate-500">Commessa:</span> {active.commessa || '—'}</div>
                  <div><span className="text-slate-500">Lotto:</span> {active.lotto || '—'}</div>
                  <div><span className="text-slate-500">Fase:</span> {active.fase_processo || '—'}</div>
                </CardContent>
              </Card>

              {(active.numero_caratteristica || active.valore_misurato) && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">Controllo collegato</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div><span className="text-slate-500">N° car.:</span> <b className="font-mono">{active.numero_caratteristica || '—'}</b></div>
                    <div className="col-span-2"><span className="text-slate-500">Descrizione:</span> {active.descrizione_caratteristica || '—'}</div>
                    <div><span className="text-slate-500">Nominale:</span> <span className="font-mono">{active.specifica_nominale || '—'}</span></div>
                    <div><span className="text-slate-500">Tol:</span> <span className="font-mono">{active.tolleranza_min || '—'} / {active.tolleranza_max || '—'}</span></div>
                    <div><span className="text-slate-500">Misurato:</span> <b className="font-mono text-red-700">{active.valore_misurato || '—'}</b></div>
                    <div className="col-span-2"><span className="text-slate-500">Esito:</span> {active.esito_controllo || '—'}</div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">Richiesta alla produzione</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p><b>Motivo:</b> {active.motivo_richiesta}</p>
                  <p><b>Attività richiesta:</b> {active.attivita_richiesta}</p>
                  {active.quantita_interessata != null && <p><b>Quantità:</b> {active.quantita_interessata}</p>}
                  {active.note_qualita && <p className="text-slate-600 italic">{active.note_qualita}</p>}
                </CardContent>
              </Card>

              {active.data_rientro_produzione && (
                <Card className="border-purple-200 bg-purple-50/40">
                  <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-purple-700"><Inbox className="w-3 h-3 inline mr-1" />Rientro produzione</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 gap-2 text-sm">
                    <div className="col-span-2"><b>Attività eseguita:</b> {active.attivita_eseguita_produzione}</div>
                    <div><b>Lavorata:</b> {active.quantita_lavorata ?? '—'}</div>
                    <div><b>Scartata:</b> {active.quantita_scartata ?? '—'}</div>
                    <div><b>Operatore:</b> {active.operatore_produzione || '—'}</div>
                    <div><b>Registrato da:</b> {active.registrato_da}</div>
                    {active.note_produzione && <div className="col-span-2 text-slate-600 italic">{active.note_produzione}</div>}
                  </CardContent>
                </Card>
              )}

              {active.esito_ricontrollo && (
                <Card className="border-amber-200 bg-amber-50/40">
                  <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-amber-700"><ClipboardCheck className="w-3 h-3 inline mr-1" />Ricontrollo qualità</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 gap-2 text-sm">
                    <div><b>Esito:</b> {active.esito_ricontrollo}</div>
                    <div><b>Ricontrollato da:</b> {active.ricontrollato_da}</div>
                    <div className="col-span-2"><b>Data:</b> {(active.data_ricontrollo || '').slice(0, 16).replace('T', ' ')}</div>
                    {active.note_ricontrollo && <div className="col-span-2 text-slate-600 italic">{active.note_ricontrollo}</div>}
                  </CardContent>
                </Card>
              )}

              {active.data_chiusura && (
                <Card className={`border-2 ${active.stato_richiesta === 'Chiusa conforme' ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'}`}>
                  <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider">{active.stato_richiesta === 'Chiusa conforme' ? <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-700" /> : <XCircle className="w-3 h-3 inline mr-1 text-red-700" />}Chiusura</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p><b>Esito finale:</b> {active.esito_finale_richiesta || active.stato_richiesta}</p>
                    <p><b>Chiuso da:</b> {active.chiuso_da} il {(active.data_chiusura || '').slice(0, 16).replace('T', ' ')}</p>
                    {active.note_chiusura && <p className="italic">{active.note_chiusura}</p>}
                    {active.motivo_annullamento && <p className="italic">Motivo annullamento: {active.motivo_annullamento}</p>}
                  </CardContent>
                </Card>
              )}

              {/* Workflow Actions */}
              {!isClosed && activeAction === null && (
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  <Button onClick={() => handleDownloadPdf(active.id, active.numero_richiesta)} data-testid="action-print" className="bg-indigo-600 hover:bg-indigo-700 text-white"><Printer className="w-3 h-3 mr-1" />Stampa PDF</Button>
                  {canCreate && !active.data_rientro_produzione && (
                    <Button variant="outline" onClick={() => setActiveAction('return')} data-testid="action-return"><Inbox className="w-3 h-3 mr-1" />Registra rientro</Button>
                  )}
                  {canCreate && active.richiede_ricontrollo_qualita && (
                    <Button variant="outline" onClick={() => setActiveAction('recontrol')} data-testid="action-recontrol"><ClipboardCheck className="w-3 h-3 mr-1" />Registra ricontrollo</Button>
                  )}
                  {canEdit && (
                    <>
                      <Button onClick={() => setActiveAction('close')} data-testid="action-close" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Chiudi</Button>
                      <Button variant="outline" onClick={() => setActiveAction('cancel')} data-testid="action-cancel"><XCircle className="w-3 h-3 mr-1" />Annulla</Button>
                    </>
                  )}
                </div>
              )}

              {/* Return form */}
              {activeAction === 'return' && (
                <Card className="border-purple-300">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Registra rientro produzione</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Attività eseguita *</Label><Textarea rows={2} value={returnForm.attivita_eseguita_produzione} onChange={e => setReturnForm({ ...returnForm, attivita_eseguita_produzione: e.target.value })} data-testid="ret-attivita" /></div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label>Qty lavorata</Label><Input type="number" value={returnForm.quantita_lavorata} onChange={e => setReturnForm({ ...returnForm, quantita_lavorata: e.target.value })} /></div>
                      <div><Label>Qty scartata</Label><Input type="number" value={returnForm.quantita_scartata} onChange={e => setReturnForm({ ...returnForm, quantita_scartata: e.target.value })} /></div>
                      <div><Label>Operatore</Label><Input value={returnForm.operatore_produzione} onChange={e => setReturnForm({ ...returnForm, operatore_produzione: e.target.value })} placeholder="Es. Mario Rossi" /></div>
                    </div>
                    <div><Label>Note produzione</Label><Textarea rows={2} value={returnForm.note_produzione} onChange={e => setReturnForm({ ...returnForm, note_produzione: e.target.value })} /></div>
                    <div className="flex gap-2"><Button variant="outline" onClick={() => setActiveAction(null)}>Annulla</Button><Button onClick={handleReturn} data-testid="ret-submit">Salva rientro</Button></div>
                  </CardContent>
                </Card>
              )}

              {/* Recontrol form */}
              {activeAction === 'recontrol' && (
                <Card className="border-amber-300">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Registra ricontrollo qualità</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Esito ricontrollo *</Label><Select value={recontrolForm.esito_ricontrollo} onValueChange={v => setRecontrolForm({ ...recontrolForm, esito_ricontrollo: v })}><SelectTrigger data-testid="rc-esito"><SelectValue /></SelectTrigger><SelectContent>{enums.esito_ricontrollo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Note ricontrollo</Label><Textarea rows={2} value={recontrolForm.note_ricontrollo} onChange={e => setRecontrolForm({ ...recontrolForm, note_ricontrollo: e.target.value })} /></div>
                    <div className="flex gap-2"><Button variant="outline" onClick={() => setActiveAction(null)}>Annulla</Button><Button onClick={handleRecontrol} data-testid="rc-submit">Salva ricontrollo</Button></div>
                  </CardContent>
                </Card>
              )}

              {/* Close form */}
              {activeAction === 'close' && (
                <Card className="border-emerald-300">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Chiusura richiesta</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Esito finale *</Label><Select value={closeForm.esito_finale_richiesta} onValueChange={v => setCloseForm({ ...closeForm, esito_finale_richiesta: v })}><SelectTrigger data-testid="close-esito"><SelectValue /></SelectTrigger><SelectContent>{enums.esito_finale_richiesta.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Note chiusura</Label><Textarea rows={2} value={closeForm.note_chiusura} onChange={e => setCloseForm({ ...closeForm, note_chiusura: e.target.value })} /></div>
                    {active.richiede_ricontrollo_qualita && !active.esito_ricontrollo && (
                      <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">⚠️ La richiesta richiede ricontrollo qualità. Compila prima la sezione &quot;Ricontrollo&quot; per chiudere conforme.</p>
                    )}
                    <div className="flex gap-2"><Button variant="outline" onClick={() => setActiveAction(null)}>Annulla</Button><Button onClick={handleClose} data-testid="close-submit">Chiudi richiesta</Button></div>
                  </CardContent>
                </Card>
              )}

              {/* Cancel form */}
              {activeAction === 'cancel' && (
                <Card className="border-red-300">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Annullamento richiesta</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Motivo annullamento *</Label><Textarea rows={2} value={cancelMotivo} onChange={e => setCancelMotivo(e.target.value)} data-testid="cancel-motivo" /></div>
                    <div className="flex gap-2"><Button variant="outline" onClick={() => setActiveAction(null)}>Indietro</Button><Button onClick={handleCancel} data-testid="cancel-submit" className="bg-red-600 hover:bg-red-700 text-white">Conferma annullamento</Button></div>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Chiudi</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual create dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Qualità a Produzione (manuale)</DialogTitle>
            <DialogDescription>Per richieste collegate ad un controllo, usa il pulsante &quot;Crea richiesta&quot; nel Registro Misure.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cliente *</Label><Input value={manualForm.customer_code} onChange={e => setManualForm({ ...manualForm, customer_code: e.target.value })} required placeholder="es. CLI001" data-testid="m-cust" /></div>
              <div><Label>Nome cliente</Label><Input value={manualForm.customer_name} onChange={e => setManualForm({ ...manualForm, customer_name: e.target.value })} /></div>
              <div><Label>Prodotto *</Label><Input value={manualForm.product_code} onChange={e => setManualForm({ ...manualForm, product_code: e.target.value })} required placeholder="es. PROD001" data-testid="m-prod" /></div>
              <div><Label>Descrizione</Label><Input value={manualForm.product_description} onChange={e => setManualForm({ ...manualForm, product_description: e.target.value })} /></div>
              <div><Label>Revisione</Label><Input value={manualForm.revision} onChange={e => setManualForm({ ...manualForm, revision: e.target.value })} /></div>
              <div><Label>ID configurazione</Label><Input value={manualForm.id_configurazione} onChange={e => setManualForm({ ...manualForm, id_configurazione: e.target.value })} /></div>
              <div><Label>Commessa</Label><Input value={manualForm.commessa} onChange={e => setManualForm({ ...manualForm, commessa: e.target.value })} /></div>
              <div><Label>Lotto</Label><Input value={manualForm.lotto} onChange={e => setManualForm({ ...manualForm, lotto: e.target.value })} /></div>
              <div><Label>Fase processo</Label><Input value={manualForm.fase_processo} onChange={e => setManualForm({ ...manualForm, fase_processo: e.target.value })} placeholder="es. OP20" /></div>
              <div><Label>Quantità interessata</Label><Input type="number" value={manualForm.quantita_interessata} onChange={e => setManualForm({ ...manualForm, quantita_interessata: e.target.value })} /></div>
            </div>
            <div><Label>Motivo richiesta *</Label><Textarea rows={2} value={manualForm.motivo_richiesta} onChange={e => setManualForm({ ...manualForm, motivo_richiesta: e.target.value })} required data-testid="m-motivo" /></div>
            <div><Label>Attività richiesta *</Label><Textarea rows={2} value={manualForm.attivita_richiesta} onChange={e => setManualForm({ ...manualForm, attivita_richiesta: e.target.value })} required data-testid="m-attivita" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Priorità *</Label><Select value={manualForm.priorita} onValueChange={v => setManualForm({ ...manualForm, priorita: v })}><SelectTrigger data-testid="m-prio"><SelectValue /></SelectTrigger><SelectContent>{enums.priorita.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Impatto operativo *</Label><Select value={manualForm.impatto_operativo} onValueChange={v => setManualForm({ ...manualForm, impatto_operativo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{enums.impatto_operativo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Scadenza</Label><Input type="date" value={manualForm.scadenza_richiesta} onChange={e => setManualForm({ ...manualForm, scadenza_richiesta: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={manualForm.richiede_ricontrollo_qualita} onCheckedChange={v => setManualForm({ ...manualForm, richiede_ricontrollo_qualita: !!v })} />Richiede ricontrollo qualità dopo intervento</label>
            <div><Label>Note qualità</Label><Textarea rows={2} value={manualForm.note_qualita} onChange={e => setManualForm({ ...manualForm, note_qualita: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setManualOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="m-submit">Crea richiesta</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
